import React from 'react';
import Icon from '../../../components/AppIcon';

const AIInsightsPanel = ({ recommendations, analysis, dashboardData }) => {
  const calculateAverageROI = () => {
    if (!recommendations?.length) return 0;
    const total = recommendations?.reduce((sum, rec) => sum + rec?.predictedROI, 0);
    return (total / recommendations?.length)?.toFixed(1);
  };

  const calculateAverageConfidence = () => {
    if (!recommendations?.length) return 0;
    const total = recommendations?.reduce((sum, rec) => sum + rec?.roiConfidence, 0);
    return (total / recommendations?.length)?.toFixed(1);
  };

  const getTopCategory = () => {
    if (!recommendations?.length) return 'N/A';
    const categories = recommendations?.map(r => r?.category);
    const counts = {};
    categories?.forEach(cat => {
      counts[cat] = (counts?.[cat] || 0) + 1;
    });
    return Object.keys(counts)?.reduce((a, b) => counts?.[a] > counts?.[b] ? a : b);
  };

  return (
    <div className="bg-white rounded-xl shadow-elevation-2 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-[#E6F0F5] rounded-lg flex items-center justify-center">
          <Icon name="Info" size={20} className="text-[#002B45]" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-[#002B45]">AI Insights & Transparency</h2>
          <p className="text-sm text-gray-500">Understanding the recommendation logic and data sources</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recommendation Statistics */}
        <div className="space-y-4">
          <h3 className="font-semibold text-[#002B45] mb-4">Recommendation Statistics</h3>
          
          <div className="bg-gradient-to-r from-[#1FB5A5]/10 to-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Average Predicted ROI</span>
              <span className="text-2xl font-bold text-[#1FB5A5]">{calculateAverageROI()}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-[#1FB5A5] h-2 rounded-full"
                style={{ width: `${Math.min(calculateAverageROI(), 100)}%` }}
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-[#E6F0F5] rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Average Confidence Level</span>
              <span className="text-2xl font-bold text-[#002B45]">{calculateAverageConfidence()}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-[#002B45] h-2 rounded-full"
                style={{ width: `${calculateAverageConfidence()}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-xs text-blue-700 mb-1">Total Recommendations</p>
              <p className="text-2xl font-bold text-blue-600">{recommendations?.length}</p>
            </div>
            <div className="bg-teal-50 rounded-lg p-4">
              <p className="text-xs text-teal-700 mb-1">Top Category</p>
              <p className="text-lg font-bold text-teal-600">{getTopCategory()}</p>
            </div>
          </div>
        </div>

        {/* Data Sources & Methodology */}
        <div className="space-y-4">
          <h3 className="font-semibold text-[#002B45] mb-4">Data Sources & Methodology</h3>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Database" size={16} className="text-[#002B45]" />
              <h4 className="font-semibold text-[#002B45] text-sm">Input Data Used</h4>
            </div>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <Icon name="CheckCircle" size={12} className="text-[#1FB5A5]" />
                <span>Current maturity score: {dashboardData?.currentMaturityScore}/100</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <Icon name="CheckCircle" size={12} className="text-[#1FB5A5]" />
                <span>5 dimension scores (infrastructure, analytics, governance, skills, alignment)</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <Icon name="CheckCircle" size={12} className="text-[#1FB5A5]" />
                <span>Consultation history: {dashboardData?.totalConsultations} sessions</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <Icon name="CheckCircle" size={12} className="text-[#1FB5A5]" />
                <span>Historical assessment data and trends</span>
              </li>
            </ul>
          </div>

          <div className="bg-[#E6F0F5] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Brain" size={16} className="text-[#002B45]" />
              <h4 className="font-semibold text-[#002B45] text-sm">AI Model Information</h4>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-[#002B45]/80">
                <Icon name="Sparkles" size={12} className="mt-0.5 flex-shrink-0" />
                <span>GPT-5 with high reasoning effort for strategic recommendations</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-[#002B45]/80">
                <Icon name="Sparkles" size={12} className="mt-0.5 flex-shrink-0" />
                <span>Industry benchmarks and best practices integrated</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-[#002B45]/80">
                <Icon name="Sparkles" size={12} className="mt-0.5 flex-shrink-0" />
                <span>ROI predictions based on historical client outcomes</span>
              </li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Icon name="AlertTriangle" size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-yellow-900 text-sm mb-1">Important Note</h4>
                <p className="text-xs text-yellow-700">
                  AI-generated recommendations are strategic guidance based on data analysis. Actual ROI may vary based on implementation quality, organizational factors, and market conditions. Consult with our team for detailed assessment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIInsightsPanel;